import React from 'react'
import clsx from 'clsx'
import {
  Button,
  ButtonBase,
  Card,
  Icon,
  LinearProgress,
  Tooltip,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { ActionsMenu } from 'app/components/ModelListPage'
import useCurrentTheme from 'app/hooks/useCurrentTheme'
import { Theme } from 'app/components/MatxTheme/themes'
import { CategoryDot } from './ModalityCategories'

const useStyles = makeStyles(({ palette }) => ({
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderTop: `4px solid ${palette.primary.main}`,
  },
  body: {
    flex: 1,
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '16px 16px 12px',
    '&:hover': { backgroundColor: palette.action.hover },
    '&:focus-visible': {
      outline: `2px solid ${palette.primary.main}`,
      outlineOffset: -2,
    },
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 16,
  },
  icon: {
    flexShrink: 0,
    width: 40,
    height: 40,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: palette.action.hover,
    color: palette.primary.main,
  },
  titleBlock: { flex: 1, minWidth: 0 },
  title: {
    margin: 0,
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  meta: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
    marginTop: 4,
    fontSize: 12,
    color: palette.text.secondary,
  },
  inactive: {
    padding: '1px 6px',
    borderRadius: 4,
    fontWeight: 600,
    border: `1px solid ${palette.divider}`,
  },
  stats: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px 16px',
    margin: 0,
  },
  stat: { margin: 0, minWidth: 0 },
  statLabel: {
    fontSize: 12,
    color: palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    '& .material-icons': { fontSize: 16 },
  },
  statValue: { margin: 0, fontSize: 20, fontWeight: 700, lineHeight: 1.4 },
  occupation: { marginTop: 16 },
  occupationHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: 8,
    marginBottom: 6,
    fontSize: 12,
    color: palette.text.secondary,
  },
  occupationValue: {
    fontSize: 14,
    fontWeight: 700,
    color: palette.text.primary,
  },
  progress: { height: 6, borderRadius: 3 },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: '4px 8px 8px 8px',
    borderTop: `1px solid ${palette.divider}`,
  },
}))

const Stat = ({ icon, label, value }) => {
  const classes = useStyles()
  return (
    <div className={classes.stat}>
      <dt className={classes.statLabel}>
        <Icon aria-hidden>{icon}</Icon>
        {label}
      </dt>
      <dd className={classes.statValue}>{value}</dd>
    </div>
  )
}

const formatPercent = (value) =>
  `${Number(value).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`

/**
 * One modality on the Modalidades page: its category, how many classes
 * it runs (and with how many teachers), its active students and how full
 * its classes are on average (`insights`, from the modality list API's
 * `?insights=true`).
 */
const ModalityCard = ({ modality, onOpen, onShowClasses, onEdit, canEdit }) => {
  const classes = useStyles()
  const currentTheme = useCurrentTheme()
  const insights = modality.insights || {}
  const hasOccupation =
    insights.average_occupation !== null &&
    insights.average_occupation !== undefined
  const isInactive = modality.status_id === 'inactive'

  return (
    <Card
      elevation={3}
      className={clsx('t-modality-card', classes.card)}
      style={
        modality.category_color
          ? { borderTopColor: modality.category_color }
          : undefined
      }
    >
      <ButtonBase
        className={classes.body}
        onClick={onOpen}
        aria-label={`Ver detalhes de ${modality.description}`}
      >
        <div className={classes.header}>
          <div className={classes.icon} aria-hidden>
            <Icon>
              {/* Tenant branding, not a feature: stays on the theme. */}
              {currentTheme === Theme.AFA
                ? 'sports_martial_arts'
                : 'sports_soccer'}
            </Icon>
          </div>
          <div className={classes.titleBlock}>
            <Tooltip title={modality.description}>
              <h3 className={classes.title}>{modality.description}</h3>
            </Tooltip>
            <div className={classes.meta}>
              <CategoryDot color={modality.category_color} />
              <span>{modality.category || 'Sem categoria'}</span>
              {isInactive && <span className={classes.inactive}>Inativa</span>}
            </div>
          </div>
        </div>

        <dl className={classes.stats}>
          <Stat
            icon="groups"
            label="Turmas ativas"
            value={insights.active_classes_count ?? 0}
          />
          <Stat
            icon="person"
            label="Professores"
            value={insights.unique_teachers_count ?? 0}
          />
          <Stat
            icon="school"
            label="Alunos ativos"
            value={insights.active_users_count ?? 0}
          />
          <Stat
            icon="event_seat"
            label="Vagas ocupadas"
            value={
              insights.total_seats
                ? `${insights.occupied_seats}/${insights.total_seats}`
                : '—'
            }
          />
        </dl>

        <div className={classes.occupation}>
          <div className={classes.occupationHeader}>
            <span>Ocupação média das turmas</span>
            <span className={classes.occupationValue}>
              {hasOccupation ? formatPercent(insights.average_occupation) : '—'}
            </span>
          </div>
          <LinearProgress
            variant="determinate"
            className={classes.progress}
            value={
              hasOccupation ? Math.min(insights.average_occupation, 100) : 0
            }
            aria-label={`Ocupação média de ${modality.description}`}
          />
        </div>
      </ButtonBase>

      <div className={classes.footer}>
        <Button
          size="small"
          color="primary"
          endIcon={<Icon>arrow_forward</Icon>}
          onClick={onShowClasses}
        >
          Ver turmas
        </Button>
        <ActionsMenu
          label={`Ações de ${modality.description}`}
          actions={[
            {
              key: 'details',
              label: 'Ver detalhes',
              icon: 'info',
              onClick: onOpen,
            },
            canEdit && {
              key: 'edit',
              label: 'Editar modalidade',
              icon: 'edit',
              onClick: onEdit,
            },
          ].filter(Boolean)}
        />
      </div>
    </Card>
  )
}

export default ModalityCard
